import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '@/utils/api';
import {
  Brain, ArrowUpRight, Target, AlertTriangle, TrendingUp, Loader2,
  Sparkles, ShieldAlert, ChevronRight,
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// ExecutiveIntelligenceHub — Layer 2 (executive cockpit) of REVA's dual-layer
// intelligence model. Aggregates the most important opportunities, risks,
// recommendations, and financial impacts across the ENTIRE portfolio and
// surfaces them on the main Dashboard.
//
// This component intentionally REUSES the Decision Center data (single source
// of truth) — there is no duplicate scoring logic.
// ─────────────────────────────────────────────────────────────────────────────

const tierStyle = (tier) => ({
  excellent: { text: 'text-[#00A86B]',   bg: 'bg-[rgba(0,168,107,0.08)]',   border: 'border-[rgba(0,168,107,0.30)]' },
  good:      { text: 'text-[#0E8C5D]',   bg: 'bg-[rgba(31,209,138,0.10)]',  border: 'border-[rgba(31,209,138,0.35)]' },
  watch:     { text: 'text-[#A07A0F]',   bg: 'bg-[rgba(212,175,55,0.10)]',  border: 'border-[rgba(212,175,55,0.40)]' },
  critical:  { text: 'text-[#B91C1C]',   bg: 'bg-[rgba(220,38,38,0.06)]',   border: 'border-[rgba(220,38,38,0.30)]' },
}[tier] || { text: 'text-[#71777E]', bg: 'bg-[#F1F3F6]', border: 'border-[#E1E6EC]' });

const fmtSAR = (n) => `SAR ${Number(n || 0).toLocaleString()}`;

export default function ExecutiveIntelligenceHub() {
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const navigate = useNavigate();
  const [data, setData] = useState({ portfolio: null, roi: null, recs: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const [p, r, rc] = await Promise.all([
          api.getPortfolioHealth().catch(() => ({ data: null })),
          api.getRoiOpportunities().catch(() => ({ data: null })),
          api.getDecisionRecommendations(5).catch(() => ({ data: { recommendations: [] } })),
        ]);
        if (mounted) setData({ portfolio: p.data, roi: r.data, recs: rc.data?.recommendations || [] });
      } finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <div className="reva-card p-5 flex items-center gap-2 text-[12px] text-[#71777E]" data-testid="exec-intel-loading">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        {isAr ? 'يجمع REVA الذكاء التنفيذي…' : 'REVA is gathering executive intelligence…'}
      </div>
    );
  }

  const { portfolio, roi, recs } = data;
  if (!portfolio || !roi) return null;

  const portColor = tierStyle(portfolio.tier);
  const topRecs = recs.slice(0, 3);
  const weakAssets = portfolio.weakest_assets || [];

  return (
    <div className="space-y-3" data-testid="exec-intel-hub">
      {/* Banner */}
      <div className="reva-card p-5 relative overflow-hidden">
        <div className="absolute -top-12 -end-12 w-40 h-40 rounded-full pointer-events-none" style={{ background: 'radial-gradient(closest-side, rgba(0,168,107,0.18), transparent 70%)' }} />
        <div className="relative flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl bg-[color:var(--reva-signal)]/12 border border-[color:var(--reva-signal)]/25 flex items-center justify-center shrink-0">
            <Brain className="h-5 w-5 text-[color:var(--reva-signal)]" strokeWidth={1.7} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10.5px] reva-mono tracking-[0.16em] uppercase text-[color:var(--reva-signal)] font-semibold">
                {isAr ? 'مركز الذكاء التنفيذي' : 'Executive Intelligence Hub'}
              </span>
              <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ${portColor.bg} ${portColor.border} ${portColor.text}`} data-testid="exec-intel-port-score">
                {isAr ? 'صحّة المحفظة' : 'Portfolio health'} <span className="font-semibold ms-1">{portfolio.score}/100</span>
              </span>
            </div>
            <div className="mt-2 text-[14px] font-semibold text-[#0E1320]" data-testid="exec-intel-roi">
              {isAr ? 'فرصة سنويّة مكتشفة:' : 'Annual opportunity uncovered:'}{' '}
              <span className="text-[color:var(--reva-signal)]">{fmtSAR(roi.total_opportunity_annual)}</span>
            </div>
            <div className="mt-1 text-[12px] text-[#4A5263] leading-relaxed">{portfolio.explanation_en}</div>
          </div>
          <button
            onClick={() => navigate('/decisions')}
            className="reva-btn-emerald-l text-[12px] py-2 px-3 shrink-0"
            data-testid="exec-intel-open"
          >
            {isAr ? 'افتح مركز القرار' : 'Open Decision Center'} <ArrowUpRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* 3-column: top recommendations · weakest assets · ROI streams */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Recommendations */}
        <div className="reva-card p-4" data-testid="exec-intel-recs">
          <div className="flex items-center gap-2 mb-3">
            <Target className="h-3.5 w-3.5 text-[color:var(--reva-signal)]" />
            <div className="text-[11px] reva-mono tracking-[0.16em] uppercase font-semibold text-[#0E1320]">
              {isAr ? 'أبرز الإجراءات' : 'Top actions'}
            </div>
            <span className="ms-auto text-[10px] text-[#71777E] reva-mono">{topRecs.length}</span>
          </div>
          {topRecs.length ? (
            <div className="space-y-2">
              {topRecs.map((r, i) => (
                <button
                  key={r.id}
                  onClick={() => navigate('/decisions')}
                  className="w-full text-start rounded-lg border border-[#ECEFF3] bg-[#FAFBFC] hover:border-[color:var(--reva-signal)]/30 hover:bg-white p-2.5 transition-colors"
                  data-testid={`exec-intel-rec-${i}`}
                >
                  <div className="text-[12px] font-semibold text-[#0E1320] leading-snug">{isAr ? r.title_ar : r.title_en}</div>
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <span className="text-[11px] font-semibold text-[color:var(--reva-signal)]">
                      {isAr ? r.expected_impact?.label_ar : r.expected_impact?.label_en}
                    </span>
                    <ChevronRight className="h-3 w-3 text-[#9CA3AF]" />
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="text-[11.5px] text-[#9CA3AF] flex items-center gap-1.5">
              <Sparkles className="h-3 w-3" /> {isAr ? 'لا توصيات الآن — المحفظة بحالة جيّدة' : 'No recommendations — portfolio healthy'}
            </div>
          )}
        </div>

        {/* Weakest assets */}
        <div className="reva-card p-4" data-testid="exec-intel-weak">
          <div className="flex items-center gap-2 mb-3">
            <ShieldAlert className="h-3.5 w-3.5 text-[#A07A0F]" />
            <div className="text-[11px] reva-mono tracking-[0.16em] uppercase font-semibold text-[#0E1320]">
              {isAr ? 'أصول تحتاج اهتمام' : 'Assets needing attention'}
            </div>
            <span className="ms-auto text-[10px] text-[#71777E] reva-mono">{weakAssets.length}</span>
          </div>
          {weakAssets.length ? (
            <div className="space-y-2">
              {weakAssets.slice(0, 3).map((a, i) => {
                const t = tierStyle(a.score >= 60 ? 'good' : a.score >= 40 ? 'watch' : 'critical');
                return (
                  <button
                    key={a.property_id}
                    onClick={() => navigate('/decisions')}
                    className={`w-full text-start rounded-lg border ${t.border} ${t.bg} px-2.5 py-2 hover:opacity-90 transition-opacity`}
                    data-testid={`exec-intel-weak-${i}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-[12px] font-semibold text-[#0E1320] truncate flex-1">{a.property_name}</div>
                      <div className={`text-[14px] font-bold ${t.text} shrink-0`}>{a.score}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="text-[11.5px] text-[#9CA3AF]">{isAr ? 'كل الأصول بصحّة جيّدة' : 'All assets are healthy'}</div>
          )}
        </div>

        {/* ROI streams */}
        <div className="reva-card p-4" data-testid="exec-intel-roi-streams">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="h-3.5 w-3.5 text-[color:var(--reva-signal)]" />
            <div className="text-[11px] reva-mono tracking-[0.16em] uppercase font-semibold text-[#0E1320]">
              {isAr ? 'مصادر الفرص' : 'Opportunity streams'}
            </div>
          </div>
          <div className="space-y-2">
            {(roi.breakdown || []).map((b) => (
              <button
                key={b.id}
                onClick={() => navigate('/decisions')}
                className="w-full text-start rounded-lg bg-[#FAFBFC] border border-[#ECEFF3] hover:border-[color:var(--reva-signal)]/30 hover:bg-white px-2.5 py-2 transition-colors"
                data-testid={`exec-intel-roi-${b.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[11px] text-[#71777E] truncate flex-1">{isAr ? b.title_ar : b.title_en}</div>
                  <div className="text-[12px] font-semibold text-[#0E1320] reva-mono shrink-0">{fmtSAR(b.amount)}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
